import MaterialTable from '@material-table/core';
import {
    AccessTime,
    Assignment,
    DoubleArrow,
    FlightOutlined,
    Info,
    WifiOff,
} from '@mui/icons-material';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import ShareLocationIcon from '@mui/icons-material/ShareLocation';
import { Box, Tooltip, useTheme } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import { BookingSupplierStatus } from 'api/models/Booking.type';
import {
    journeyJobStatusSelector,
    MANUAL_INTERVENTION_STATUS_LABEL,
} from 'api/models/Common/JyrneyEnums';
import { getUserToken } from 'api/utility/localStorageFunctions';
import isSelectedService from 'components/Bookings/Common/isSelectedService';
import DisplayDate from 'components/Common/DisplayDates/DisplayDate';
import StatusChip from 'components/UI/StatusChip';
import useDebounce from 'hooks/useDebounce';
import { DateTime } from 'luxon';
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import {
    bookedTimeWithEta,
    expectedArrivalStatusDisplay,
} from '../Common/expectedArrivalDisplay';
import {
    getMockBookingStatusPage,
    isBookingStatusMockModeEnabled,
} from '../mockBookingStatusData';
import Toolbar from './Toolbar';

type TableProps = {
  setPageData: (s: any) => void
  setSelectedJob: (s: any) => void
  selectedJob: any
  isModalOpen: boolean
  layoutRecalcToken?: number
}

const PROGRESS_FILTER: string[] = [
  'Allocated',
  'Confirmed',
  'OnRoute',
  'Arrived',
  'Passenger_Picked_Up',
  'Passenger_Dropped_Off',
  'OnRoute_Passenger_On_Board',
  'Primary_Reallocation_Pending',
  'Driver_Allocated',
  'Manual_Reallocation_Pending',
  'Manual_Intervention_Required',
  'Booking_Update_Pending',
  'No_Show',
]

const HIDE_STALE_MANUAL_INTERVENTION_KEY =
  'bookingStatus.hideStaleManualIntervention'
const DEFAULT_ROW_HEIGHT = 48
const DEFAULT_HEADER_HEIGHT = 56
const DEFAULT_PAGINATION_HEIGHT = 56

const JOB_REF_COLUMN_INDEX = 0
const FLAGS_COLUMN_INDEX = 1

const JOB_REF_COLUMN_WIDTH = 90

const COLUMN_WEIGHTS = [0, 0, 11, 11, 11, 11, 12, 11, 14]

const FLEX_WEIGHT_TOTAL = COLUMN_WEIGHTS.reduce(
  (sum, weight, idx) =>
    idx === JOB_REF_COLUMN_INDEX || idx === FLAGS_COLUMN_INDEX
      ? sum
      : sum + weight,
  0,
)

const FLAG_ICON_WIDTH = 20
const FLAG_ICON_GAP = 2
const FLAGS_CELL_PADDING = 18
const FLAGS_HEADER_MIN_WIDTH = 52

const getFlagsColumnWidth = (iconCount: number) => {
  const count = Math.max(0, iconCount)
  const contentWidth =
    count > 0
      ? count * FLAG_ICON_WIDTH +
        (count - 1) * FLAG_ICON_GAP +
        FLAGS_CELL_PADDING
      : FLAGS_CELL_PADDING
  return Math.max(FLAGS_HEADER_MIN_WIDTH, contentWidth)
}

const getColumnWidths = (flagsIconCap: number) => {
  const flagsWidth = getFlagsColumnWidth(flagsIconCap)
  const fixedWidth = JOB_REF_COLUMN_WIDTH + flagsWidth
  return COLUMN_WEIGHTS.map((weight, idx) => {
    if (idx === JOB_REF_COLUMN_INDEX) return `${JOB_REF_COLUMN_WIDTH}px`
    if (idx === FLAGS_COLUMN_INDEX) return `${flagsWidth}px`
    return `calc((100% - ${fixedWidth}px) * ${(
      weight / FLEX_WEIGHT_TOTAL
    ).toFixed(4)})`
  })
}

const getRowFlags = (rowData: any) => {
  const flightNumber = String(
    rowData.flightDetails?.flightNumber ?? rowData.flightNumber ?? '',
  ).trim()
  return {
    showMeetAndGreetIcon: isSelectedService({
      services: rowData.services,
      selectedService: 'MeetAndGreet',
    }),
    showDestinationSupplierIcon: rowData.supplier?.isDestinationLogicSupplier,
    isFlexibleBooking: rowData.journeyTypeName === 'Flexible',
    isImmediateJourney: rowData.isImmediateJourney === true,
    flightNumber,
    showFlightNumberIcon:
      flightNumber !== '' && flightNumber.toUpperCase() !== 'N/A',
    isOnwardJourney: rowData.isOnwardJourney === true,
    isOfflineBooking: rowData.supplier?.isOffline === true,
  }
}

const countRowFlags = (rowData: any) => {
  const flags = getRowFlags(rowData)
  return [
    flags.showMeetAndGreetIcon,
    flags.showDestinationSupplierIcon,
    flags.isFlexibleBooking,
    flags.isImmediateJourney,
    flags.showFlightNumberIcon,
    flags.isOnwardJourney,
    flags.isOfflineBooking,
  ].filter(Boolean).length
}

const getFlagLineWidth = (count: number) =>
  count > 3 && count % 2 === 1 ? Math.ceil(count / 2) : count

const COLUMN_HEADERS: Array<{ title: string; sortable: boolean }> = [
  { title: 'JOB REF', sortable: true },
  { title: 'FLAGS', sortable: false },
  { title: 'PASSENGER', sortable: true },
  { title: 'CLIENT', sortable: true },
  { title: 'DESTINATION TIME', sortable: true },
  { title: 'YOUR TIMEZONE', sortable: true },
  { title: 'PROGRESS', sortable: true },
  { title: 'EXPECTED', sortable: false },
  { title: 'STATUS', sortable: true },
]

type SortState = {
  columnId: number
  direction: 'asc' | 'desc'
}

type StaleManualInterventionPreference = {
  hide: boolean
  expiresAt: string
}

const readHideStaleManualInterventionPreference = () => {
  if (typeof window === 'undefined') return false
  try {
    const raw = window.sessionStorage.getItem(
      HIDE_STALE_MANUAL_INTERVENTION_KEY,
    )
    if (!raw) return false
    const parsed = JSON.parse(raw) as StaleManualInterventionPreference
    if (!parsed?.expiresAt) return false
    const expiresAt = DateTime.fromISO(parsed.expiresAt)
    if (!expiresAt.isValid || expiresAt < DateTime.now()) {
      window.sessionStorage.removeItem(HIDE_STALE_MANUAL_INTERVENTION_KEY)
      return false
    }
    return Boolean(parsed.hide)
  } catch (error) {
    console.error(
      '[BookingStatusPage/Table] Failed to read stale manual intervention preference:',
      error,
    )
    return false
  }
}

const writeHideStaleManualInterventionPreference = (hide: boolean) => {
  if (typeof window === 'undefined') return
  try {
    if (!hide) {
      window.sessionStorage.removeItem(HIDE_STALE_MANUAL_INTERVENTION_KEY)
      return
    }
    const expiresAt = DateTime.now().endOf('day').toISO()
    window.sessionStorage.setItem(
      HIDE_STALE_MANUAL_INTERVENTION_KEY,
      JSON.stringify({ hide, expiresAt }),
    )
  } catch (error) {
    console.error(
      '[BookingStatusPage/Table] Failed to write stale manual intervention preference:',
      error,
    )
  }
}

const isStableScrollbarGutterSupported = () => {
  if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function') {
    return false
  }

  return CSS.supports('scrollbar-gutter: stable')
}

const getAverageRenderedRowHeight = (table: Element | null) => {
  if (!table) return 0

  const bodyRows = Array.from(table.querySelectorAll('tbody tr'))
  if (!bodyRows.length) return 0

  let totalRowHeight = 0
  bodyRows.forEach((row) => {
    totalRowHeight += row.getBoundingClientRect().height
  })

  return totalRowHeight / bodyRows.length
}

const buildPageSizeOptions = (basePageSize: number, allPageSize: number) => {
  const normalizedBasePageSize = Math.max(1, basePageSize)
  const normalizedAllPageSize = Math.max(normalizedBasePageSize, allPageSize)

  return Array.from(
    new Set([
      normalizedBasePageSize,
      normalizedBasePageSize * 2,
      normalizedAllPageSize,
    ]),
  ).sort((left, right) => left - right)
}

const calculateRowsThatFit = ({
  containerHeight,
  toolbarHeight,
  paginationHeight,
  headerHeight,
  rowHeight,
}: {
  containerHeight: number
  toolbarHeight: number
  paginationHeight: number
  headerHeight: number
  rowHeight: number
}) => {
  if (containerHeight <= 0 || rowHeight <= 0) {
    return null
  }

  const tableAreaHeight = Math.max(
    containerHeight - toolbarHeight - paginationHeight - 2,
    headerHeight + rowHeight * 2,
  )
  const availableRowHeight = Math.max(tableAreaHeight - headerHeight, rowHeight)
  const bodyOnlyHeight = Math.max(tableAreaHeight - headerHeight, rowHeight * 2)

  return {
    rows: Math.max(1, Math.floor(availableRowHeight / rowHeight)),
    tableBodyHeight: bodyOnlyHeight,
  }
}

const Table = ({
  setPageData,
  setSelectedJob,
  selectedJob,
  isModalOpen,
  layoutRecalcToken,
}: TableProps) => {
  const muiTheme = useTheme() as any
  const headerColour =
    muiTheme.components?.journeyStatus?.headerColour ??
    muiTheme.palette.grey[300]
  const headerTextColour =
    muiTheme.components?.journeyStatus?.headerTextColour ??
    muiTheme.palette.getContrastText(headerColour)
  const [searchText, setSearchText] = useState<string>('')
  const debouncedSearch = useDebounce(searchText, 300)
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([])
  const debouncedStatuses = useDebounce(selectedStatuses, 500)
  const [selectedJobTypes, setSelectedJobTypes] = useState<string[]>([
    'Standard',
    'Flexible',
    'DestinationSupplier',
    'MeetAndGreet',
  ])
  const debouncedJobTypes = useDebounce(selectedJobTypes, 500)
  const [selectedClient, setSelectedClient] = useState('All')
  const debouncedClient = useDebounce(selectedClient, 100)
  const tableRef = useRef<any>(null)
  const tableContainerRef = useRef<HTMLDivElement | null>(null)
  const [tableLoading, setTableLoading] = useState(false)
  const [hideStaleManualIntervention, setHideStaleManualIntervention] =
    useState<boolean>(() => readHideStaleManualInterventionPreference())
  const [staleManualInterventionCount, setStaleManualInterventionCount] =
    useState<number>(0)
  const [calculatedPageSize, setCalculatedPageSize] = useState<number>(10)
  const [totalRowCount, setTotalRowCount] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)
  const [isInitialLayoutReady, setIsInitialLayoutReady] = useState(false)
  const [visibleRowCount, setVisibleRowCount] = useState(0)
  const [visibleJobReferences, setVisibleJobReferences] = useState<string[]>([])
  const [maxFlagLineWidth, setMaxFlagLineWidth] = useState(0)
  const [tableBodyHeight, setTableBodyHeight] = useState<number | undefined>(
    undefined,
  )
  const [sortState, setSortState] = useState<SortState>({
    columnId: 5,
    direction: 'asc',
  })
  const sortStateRef = useRef<SortState>(sortState)
  const [scrollbarGutterWidth, setScrollbarGutterWidth] = useState<number>(0)
  const customHeaderRef = useRef<HTMLDivElement | null>(null)
  const isMountedRef = useRef(true)
  const pageSizeRef = useRef(pageSize)
  const pageSizeMultiplierRef = useRef(1)
  const pageSizeModeRef = useRef<'multiple' | 'all'>('multiple')
  const rowHeightRef = useRef(DEFAULT_ROW_HEIGHT)
  const totalRowCountRef = useRef(0)
  const stabilizedAtWidthRef = useRef<number | null>(null)
  const calculatedPageSizeRef = useRef(0)
  const staleMITotalCountRef = useRef(0)
  const hasLoadedDataRef = useRef(false)
  const recalculateTableLayoutRef = useRef<() => void>(() => {})
  const currentPageRef = useRef(0)
  const isModalOpenRef = useRef(isModalOpen)
  const pendingFilterReloadRef = useRef(false)
  const pendingLayoutReloadRef = useRef(false)
  const lastQueryResultRef = useRef<{
    data: any[]
    page: number
    totalCount: number
  }>({
    data: [],
    page: 0,
    totalCount: 0,
  })

  const clearSelection = useCallback(() => {
    setSelectedJob(null)
  }, [setSelectedJob])

  const handleHeaderSortClick = useCallback((columnId: number) => {
    if (!COLUMN_HEADERS[columnId]?.sortable) return
    setSortState((previous) =>
      previous.columnId === columnId
        ? {
            columnId,
            direction: previous.direction === 'asc' ? 'desc' : 'asc',
          }
        : { columnId, direction: 'asc' },
    )
  }, [])

  useEffect(() => {
    sortStateRef.current = sortState
    if (isModalOpenRef.current) {
      pendingFilterReloadRef.current = true
      return
    }
    if (tableRef.current?.onQueryChange) {
      tableRef.current.onQueryChange({
        page: 0,
        pageSize: pageSizeRef.current,
      })
    }
  }, [sortState])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    pageSizeRef.current = pageSize
  }, [pageSize])

  useEffect(() => {
    isModalOpenRef.current = isModalOpen
  }, [isModalOpen])

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return
    if (isInitialLayoutReady) return

    let frameId = 0

    const measureInitialLayout = () => {
      const container = tableContainerRef.current
      if (!container) {
        frameId = window.requestAnimationFrame(measureInitialLayout)
        return
      }

      const toolbarHeight =
        container
          .querySelector('[data-booking-table-toolbar="true"]')
          ?.getBoundingClientRect().height ?? 0

      const layout = calculateRowsThatFit({
        containerHeight: container.getBoundingClientRect().height,
        toolbarHeight,
        paginationHeight: DEFAULT_PAGINATION_HEIGHT,
        headerHeight: DEFAULT_HEADER_HEIGHT,
        rowHeight: rowHeightRef.current,
      })

      if (!layout) {
        frameId = window.requestAnimationFrame(measureInitialLayout)
        return
      }

      pageSizeRef.current = layout.rows
      calculatedPageSizeRef.current = layout.rows
      setCalculatedPageSize(layout.rows)
      setPageSize(layout.rows)
      setTableBodyHeight(layout.tableBodyHeight)
      setIsInitialLayoutReady(true)
    }

    measureInitialLayout()

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId)
      }
    }
  }, [isInitialLayoutReady])

  const recalculateTableLayout = useCallback(() => {
    const container = tableContainerRef.current
    if (!container) return

    const containerRect = container.getBoundingClientRect()
    const containerWidth = containerRect.width
    const containerHeight = containerRect.height
    if (containerHeight <= 0 || containerWidth <= 0) return

    const table = container.querySelector('table')
    const measuredRowHeight = getAverageRenderedRowHeight(table)
    const hasRenderedRows = measuredRowHeight > 0

    // Only trust row measurements once real data has been fetched.
    // Before that, tbody may contain a tall "No records" placeholder row
    // which would produce a wildly wrong row-height estimate.
    const hasRealData = hasLoadedDataRef.current

    // Check if we have already stabilized at this exact width with real rows.
    // If so, skip the recalculation to prevent measurement→pageSize→render loops.
    const isStabilizedAtCurrentWidth =
      stabilizedAtWidthRef.current !== null &&
      Math.abs(containerWidth - stabilizedAtWidthRef.current) <= 1

    if (isStabilizedAtCurrentWidth && hasRenderedRows && hasRealData) {
      return
    }

    const toolbarHeight =
      container
        .querySelector('[data-booking-table-toolbar="true"]')
        ?.getBoundingClientRect().height ?? 0
    const paginationHeight =
      container
        .querySelector('.MuiTablePagination-root')
        ?.getBoundingClientRect().height ?? DEFAULT_PAGINATION_HEIGHT
    const headerHeight =
      table?.querySelector('thead')?.getBoundingClientRect().height ??
      DEFAULT_HEADER_HEIGHT

    // Prefer the measured row height, but only when real data rows are present.
    // Before data loads, the tbody placeholder row is misleadingly tall.
    const canTrustMeasurement = hasRenderedRows && hasRealData
    const rowHeight = canTrustMeasurement
      ? measuredRowHeight
      : rowHeightRef.current
    if (canTrustMeasurement) {
      rowHeightRef.current = measuredRowHeight
    }
    if (rowHeight <= 0) return

    const layout = calculateRowsThatFit({
      containerHeight,
      toolbarHeight,
      paginationHeight,
      headerHeight,
      rowHeight,
    })
    if (!layout) return

    let { rows, tableBodyHeight: nextTableBodyHeight } = layout

    // Width-monotonic constraint (requirements 8 & 9):
    //   - narrower panel  → rows per page can only stay the same or decrease
    //   - wider panel     → rows per page can only stay the same or increase
    const previousWidth = stabilizedAtWidthRef.current
    const previousBaseSize = calculatedPageSizeRef.current
    if (previousWidth !== null && previousBaseSize > 0 && canTrustMeasurement) {
      if (containerWidth < previousWidth - 1) {
        rows = Math.min(rows, previousBaseSize)
      } else if (containerWidth > previousWidth + 1) {
        rows = Math.max(rows, previousBaseSize)
      }
    }

    // Mark stabilized once we have measured real data rows (not placeholders).
    if (canTrustMeasurement) {
      stabilizedAtWidthRef.current = containerWidth
    }

    calculatedPageSizeRef.current = rows

    setCalculatedPageSize((previousSize) =>
      previousSize === rows ? previousSize : rows,
    )
    setTableBodyHeight((previousHeight) =>
      previousHeight === nextTableBodyHeight
        ? previousHeight
        : nextTableBodyHeight,
    )

    const currentTotalRowCount = totalRowCountRef.current
    const nextPageSize =
      pageSizeModeRef.current === 'all'
        ? Math.max(currentTotalRowCount, rows)
        : rows * pageSizeMultiplierRef.current

    if (nextPageSize === pageSizeRef.current) return

    pageSizeRef.current = nextPageSize
    setPageSize(nextPageSize)

    if (isModalOpenRef.current) {
      pendingLayoutReloadRef.current = true
      return
    }

    if (tableRef.current?.onQueryChange) {
      tableRef.current.onQueryChange({
        page: currentPageRef.current,
        pageSize: nextPageSize,
      })
    }
  }, [])

  useEffect(() => {
    recalculateTableLayoutRef.current = recalculateTableLayout
  }, [recalculateTableLayout])

  // Observe container size for panel resize / window resize / zoom changes.
  // Intentionally free of data-dependent deps to avoid feedback loops.
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!isInitialLayoutReady) return

    let frameId = 0
    const scheduleLayoutRecalculation = () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId)
      }
      frameId = window.requestAnimationFrame(() => {
        // Width may have changed – clear stabilization so we re-measure.
        const container = tableContainerRef.current
        if (container) {
          const currentWidth = container.getBoundingClientRect().width
          if (
            stabilizedAtWidthRef.current !== null &&
            Math.abs(currentWidth - stabilizedAtWidthRef.current) > 1
          ) {
            stabilizedAtWidthRef.current = null
          }
        }
        recalculateTableLayoutRef.current()
      })
    }

    const container = tableContainerRef.current
    if (!container || typeof ResizeObserver === 'undefined') {
      return () => {
        if (frameId) {
          window.cancelAnimationFrame(frameId)
        }
      }
    }

    const resizeObserver = new ResizeObserver(() => {
      scheduleLayoutRecalculation()
    })

    resizeObserver.observe(container)

    window.addEventListener('resize', scheduleLayoutRecalculation)

    return () => {
      window.removeEventListener('resize', scheduleLayoutRecalculation)
      resizeObserver.disconnect()
      if (frameId) {
        window.cancelAnimationFrame(frameId)
      }
    }
  }, [isInitialLayoutReady])

  useEffect(() => {
    const tableBodyContainer = tableRef.current?.tableContainerDiv?.current
    if (!tableBodyContainer) return
    if (!isStableScrollbarGutterSupported()) return

    // Reserve the scrollbar gutter so row wrapping/page-size calculations
    // do not oscillate when the browser decides to show or hide overflow.
    tableBodyContainer.style.scrollbarGutter = 'stable'
  }, [pageSize, tableLoading])

  useEffect(() => {
    const tableBodyContainer = tableRef.current?.tableContainerDiv?.current
    if (!tableBodyContainer) return
    const measure = () => {
      const gutter =
        tableBodyContainer.offsetWidth - tableBodyContainer.clientWidth
      setScrollbarGutterWidth((previous) =>
        previous === gutter ? previous : gutter,
      )
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(tableBodyContainer)
    return () => observer.disconnect()
  }, [pageSize, tableLoading, tableBodyHeight])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (layoutRecalcToken == null) return

    // Panel resize completed – clear stabilization so the next recalculate
    // re-measures at the (potentially) new width.
    stabilizedAtWidthRef.current = null

    let firstFrameId = 0
    let secondFrameId = 0

    firstFrameId = window.requestAnimationFrame(() => {
      recalculateTableLayoutRef.current()
      secondFrameId = window.requestAnimationFrame(() => {
        recalculateTableLayoutRef.current()
      })
    })

    return () => {
      window.cancelAnimationFrame(firstFrameId)
      window.cancelAnimationFrame(secondFrameId)
    }
  }, [layoutRecalcToken])

  // After the very first data load renders rows, run one measurement pass
  // so the page size is based on actual (not default) row heights.
  useEffect(() => {
    if (!isInitialLayoutReady) return
    // Already stabilized with real row data – nothing to do.
    if (stabilizedAtWidthRef.current !== null) return
    if (visibleRowCount === 0) return

    // Double-rAF ensures the browser has committed the layout.
    let outerFrameId = 0
    let innerFrameId = 0
    outerFrameId = window.requestAnimationFrame(() => {
      innerFrameId = window.requestAnimationFrame(() => {
        recalculateTableLayoutRef.current()
      })
    })

    return () => {
      window.cancelAnimationFrame(outerFrameId)
      window.cancelAnimationFrame(innerFrameId)
    }
  }, [isInitialLayoutReady, visibleRowCount])

  const handleHideStaleManualInterventionChange = useCallback(
    (value: boolean) => {
      setHideStaleManualIntervention(value)
      writeHideStaleManualInterventionPreference(value)
      if (isModalOpenRef.current) {
        pendingFilterReloadRef.current = true
        return
      }
      if (tableRef.current?.onQueryChange) {
        tableRef.current.onQueryChange({
          page: 0,
          pageSize: pageSizeRef.current,
        })
      }
    },
    [],
  )

  const filterByJobType = (rowData: any, jobTypes: string[]) => {
    if (jobTypes.includes('Flexible') && rowData.journeyTypeName === 'Flexible')
      return true

    if (
      jobTypes.includes('DestinationSupplier') &&
      rowData.supplier?.isDestinationLogicSupplier
    )
      return true

    if (
      jobTypes.includes('MeetAndGreet') &&
      isSelectedService({
        services: rowData.services,
        selectedService: 'MeetAndGreet',
      })
    )
      return true

    if (jobTypes.includes('Standard')) {
      const isFlexible = rowData.journeyTypeName === 'Flexible'
      const isDestinationSupplier = rowData.supplier?.isDestinationLogicSupplier
      const isMeetAndGreet = isSelectedService({
        services: rowData.services,
        selectedService: 'MeetAndGreet',
      })
      if (!isFlexible && !isDestinationSupplier && !isMeetAndGreet) return true
    }

    return false
  }

  const fetchTableData = useCallback(
    async (query: any) => {
      if (isModalOpenRef.current) {
        return lastQueryResultRef.current
      }

      if (isMountedRef.current) {
        setTableLoading(true)
      }
      const effectivePage = query?.page ?? 0
      const effectiveLimit = query?.pageSize ?? 10
      try {
        const status: string[] = []
        Object.entries(journeyJobStatusSelector)?.forEach(([value, label]) => {
          if (debouncedStatuses.indexOf(label as string) > -1) {
            status.push(value)
          }
        })

        if (
          debouncedStatuses.includes(MANUAL_INTERVENTION_STATUS_LABEL) &&
          !status.includes('Attention_Needed')
        ) {
          status.push('Attention_Needed')
        }

        let jobOrderByTypeName = 'When'
        const orderedColumnId = sortStateRef.current.columnId
        switch (orderedColumnId) {
          case 0:
            jobOrderByTypeName = 'JobReference'
            break
          case 2:
            jobOrderByTypeName = 'PassengerName'
            break
          case 3:
            jobOrderByTypeName = 'ClientName'
            break
          case 4:
          case 5:
          case 7:
            jobOrderByTypeName = 'When'
            break
          case 6:
            jobOrderByTypeName = 'Progress'
            break
          case 8:
            jobOrderByTypeName = 'Status'
            break
          default:
            jobOrderByTypeName = 'When'
            break
        }

        // When stale MI is hidden, exclude Manual_Intervention_Required from
        // the server-side progress filter so the API returns only non-MI jobs.
        // This keeps server pagination accurate and fills the table completely.
        const progressFilter = hideStaleManualIntervention
          ? PROGRESS_FILTER.filter((p) => p !== 'Manual_Intervention_Required')
          : PROGRESS_FILTER

        const payload: any = {
          status,
          search: debouncedSearch,
          progress: progressFilter,
          orderBy: {
            isDescending: sortStateRef.current.direction === 'desc',
            jobOrderByTypeName,
          },
        }

        if (debouncedClient !== 'All') {
          payload.clientName = debouncedClient
        }

        const json = isBookingStatusMockModeEnabled()
          ? getMockBookingStatusPage({
              limit: effectiveLimit,
              offset: effectivePage * effectiveLimit,
              payload,
            })
          : await (async () => {
              const token = await getUserToken('bookings')
              const res = await fetch(
                `${
                  process.env.REACT_APP_BASE_API_URL
                }/bookings-api/jobs/search?limit=${effectiveLimit}${
                  effectivePage >= 1
                    ? `&offset=${effectivePage * effectiveLimit}`
                    : ``
                }`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({ data: payload }),
                },
              )

              if (!res.ok) {
                throw new Error(
                  `Failed to load bookings data (status ${res.status})`,
                )
              }

              return res.json()
            })()

        if (!json) {
          throw new Error('Failed to load bookings data')
        }
        const rawValue = json?.value
        const value: any[] = Array.isArray(rawValue) ? rawValue : []
        hasLoadedDataRef.current = true
        const selectedJobId = selectedJob?.id
        if (isMountedRef.current) {
          setPageData(value)
          if (selectedJobId && value.length > 0) {
            const updated = value.find(
              (o: any) => String(o.id) === String(selectedJobId),
            )
            if (updated) setSelectedJob(updated)
          }
        }
        // Stale MI exclusion is now handled server-side via the progress
        // filter (see progressFilter above).  The stale MI count shown in
        // the toolbar badge is maintained by the separate fetchStaleMICount
        // call, so we no longer need client-side stale-MI detection here.
        const filtered =
          value.filter((row: any) => filterByJobType(row, debouncedJobTypes)) ??
          []
        if (isMountedRef.current) {
          setVisibleRowCount(filtered.length)
          setVisibleJobReferences(
            filtered
              .map((row: any) => String(row?.jobReference ?? '').trim())
              .filter((jobReference) => jobReference !== ''),
          )
          setMaxFlagLineWidth(
            filtered.reduce(
              (max: number, row: any) =>
                Math.max(max, getFlagLineWidth(countRowFlags(row))),
              0,
            ),
          )
        }
        const result = {
          data: filtered,
          page: effectivePage,
          totalCount:
            typeof json?.size === 'number' ? json.size : filtered.length,
        }
        if (isMountedRef.current) {
          totalRowCountRef.current = result.totalCount
          setTotalRowCount(result.totalCount)

          // In "all" mode the page size must track the total row count so
          // every row is fetched, even when the total changes between polls.
          if (pageSizeModeRef.current === 'all') {
            const base = calculatedPageSizeRef.current || 10
            const allSize = Math.max(result.totalCount, base)
            if (allSize !== pageSizeRef.current) {
              pageSizeRef.current = allSize
              setPageSize(allSize)
            }
          }
        }
        currentPageRef.current = effectivePage
        lastQueryResultRef.current = result
        return result
      } catch (error) {
        console.error('[BookingStatusPage/Table] fetchTableData failed:', error)
        return lastQueryResultRef.current
      } finally {
        if (isMountedRef.current) {
          setTableLoading(false)
        }
      }
    },
    [
      debouncedSearch,
      debouncedClient,
      selectedJob?.id,
      debouncedJobTypes,
      debouncedStatuses,
      hideStaleManualIntervention,
      setPageData,
      setSelectedJob,
    ],
  )

  const copyVisibleJobReferences = useCallback(async () => {
    if (!visibleJobReferences.length) {
      return false
    }

    const content = visibleJobReferences.join('\n')
    if (!navigator.clipboard?.writeText) {
      console.warn(
        '[BookingStatusPage/Table] Clipboard API unavailable in this context',
      )
      return false
    }

    try {
      await navigator.clipboard.writeText(content)
      return true
    } catch (error) {
      console.warn(
        '[BookingStatusPage/Table] navigator.clipboard.writeText failed:',
        error,
      )
      return false
    }
  }, [visibleJobReferences])

  const triggerTableReload = useCallback(() => {
    if (isModalOpenRef.current) return
    if (tableRef.current?.onQueryChange) {
      tableRef.current.onQueryChange()
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const interval = window.setInterval(() => {
      if (
        typeof document !== 'undefined' &&
        document.visibilityState !== 'visible'
      ) {
        return
      }
      triggerTableReload()
    }, 10000)
    return () => {
      window.clearInterval(interval)
    }
  }, [triggerTableReload])

  // ── Stale Manual-Intervention count (separate, less-frequent call) ──
  const fetchStaleMICount = useCallback(async () => {
    if (isModalOpenRef.current) return

    try {
      const now = DateTime.now()
      const staleCutoff = now.minus({ days: 1 }).toUTC().toISO()
      const farPast = now.minus({ years: 10 }).toUTC().toISO()

      const status: string[] = []
      Object.entries(journeyJobStatusSelector)?.forEach(([value, label]) => {
        if (debouncedStatuses.indexOf(label as string) > -1) {
          status.push(value)
        }
      })
      if (
        debouncedStatuses.includes(MANUAL_INTERVENTION_STATUS_LABEL) &&
        !status.includes('Attention_Needed')
      ) {
        status.push('Attention_Needed')
      }

      const payload: any = {
        status,
        search: debouncedSearch,
        progress: ['Manual_Intervention_Required'],
        orderBy: { isDescending: false, jobOrderByTypeName: 'When' },
        when: { from: farPast, to: staleCutoff },
      }

      if (debouncedClient !== 'All') {
        payload.clientName = debouncedClient
      }

      const json = isBookingStatusMockModeEnabled()
        ? getMockBookingStatusPage({ limit: 1, payload })
        : await (async () => {
            const token = await getUserToken('bookings')
            const res = await fetch(
              `${process.env.REACT_APP_BASE_API_URL}/bookings-api/jobs/search?limit=1`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ data: payload }),
              },
            )

            if (!res.ok) {
              throw new Error(
                `Failed to fetch stale MI count (status ${res.status})`,
              )
            }

            return res.json()
          })()

      const count = typeof json?.size === 'number' ? json.size : 0
      staleMITotalCountRef.current = count
      if (isMountedRef.current) {
        setStaleManualInterventionCount(count)
      }
    } catch (error) {
      console.error(
        '[BookingStatusPage/Table] fetchStaleMICount failed:',
        error,
      )
    }
  }, [debouncedSearch, debouncedStatuses, debouncedClient])

  // Poll the stale MI count every 60 seconds (and immediately on mount / filter change).
  useEffect(() => {
    if (typeof window === 'undefined') return

    fetchStaleMICount()

    const interval = window.setInterval(() => {
      if (
        typeof document !== 'undefined' &&
        document.visibilityState !== 'visible'
      ) {
        return
      }
      fetchStaleMICount()
    }, 60000)

    return () => {
      window.clearInterval(interval)
    }
  }, [fetchStaleMICount])

  useEffect(() => {
    if (isModalOpenRef.current) {
      pendingFilterReloadRef.current = true
      return
    }
    if (tableRef.current?.onQueryChange) {
      tableRef.current.onQueryChange({
        page: 0,
        pageSize: pageSizeRef.current,
      })
    }
  }, [
    debouncedSearch,
    debouncedClient,
    debouncedStatuses,
    debouncedJobTypes,
    hideStaleManualIntervention,
  ])

  useEffect(() => {
    if (isModalOpen) return
    if (!tableRef.current?.onQueryChange) return

    if (pendingFilterReloadRef.current) {
      pendingFilterReloadRef.current = false
      pendingLayoutReloadRef.current = false
      tableRef.current.onQueryChange({
        page: 0,
        pageSize: pageSizeRef.current,
      })
      return
    }

    if (pendingLayoutReloadRef.current) {
      pendingLayoutReloadRef.current = false
      tableRef.current.onQueryChange({
        page: currentPageRef.current,
        pageSize: pageSizeRef.current,
      })
    }
  }, [isModalOpen])

  const handleTableAreaClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement

      if (
        target.closest('tbody tr') ||
        target.closest('thead') ||
        target.closest('.MuiTablePagination-root') ||
        target.closest('[data-booking-table-toolbar="true"]') ||
        target.closest('button') ||
        target.closest('input')
      ) {
        return
      }

      clearSelection()
    },
    [clearSelection],
  )

  const allPageSize = Math.max(totalRowCount, calculatedPageSize)
  const pageSizeOptions = buildPageSizeOptions(calculatedPageSize, allPageSize)
  const columnWidths = getColumnWidths(maxFlagLineWidth)

  return (
    <Box
      onClickCapture={handleTableAreaClick}
      ref={tableContainerRef}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '.MuiTableCell-footer': {
          backgroundColor: headerColour,
        },
        '.MuiPaper-root': {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
          borderRadius: 0,
          boxShadow: 'none',
        },
        '.MuiTableSortLabel-root, .MuiTableSortLabel-root:hover, .MuiTableSortLabel-root.Mui-active':
          {
            color: `${headerTextColour} !important`,
          },
        '.MuiTableSortLabel-icon': {
          color: `${headerTextColour} !important`,
          opacity: 1,
        },
        '.MuiPaper-root > .MuiBox-root': {
          flex: 1,
          minHeight: 0,
        },
        '.MuiTable-root': {
          tableLayout: 'fixed',
          backgroundColor: '#F8F8F8',
        },
        '.MuiPaper-root, .MuiPaper-root > .MuiBox-root': {
          backgroundColor: '#F8F8F8',
        },
        '.MuiTable-root > thead': {
          display: 'none',
        },
        '.MuiTablePagination-root': {
          marginTop: 'auto',
          borderTop: '1px solid rgba(0, 0, 0, 0.08)',
          backgroundColor: headerColour,
          color: headerTextColour,
        },
        '.MuiTablePagination-root .MuiTablePagination-selectLabel, .MuiTablePagination-root .MuiTablePagination-displayedRows, .MuiTablePagination-root .MuiTablePagination-select, .MuiTablePagination-root .MuiTablePagination-selectIcon, .MuiTablePagination-root .MuiIconButton-root':
          {
            color: headerTextColour,
          },
        '.MuiTablePagination-root .MuiIconButton-root.Mui-disabled': {
          color: headerTextColour,
          opacity: 0.4,
        },
        '.MuiTablePagination-toolbar': {
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '56px',
        },
        '.MuiTablePagination-spacer': {
          flex: 'none',
        },
        '.MuiTableCell-root': {
          padding: '8px 5px',
        },
      }}
    >
      <Box data-booking-table-toolbar="true">
        <Toolbar
          selectedStatuses={selectedStatuses}
          SetSelectedStatuses={setSelectedStatuses}
          selectedJobTypes={selectedJobTypes}
          setSelectedJobTypes={setSelectedJobTypes}
          selectedClient={selectedClient}
          setSelectedClient={setSelectedClient}
          searchText={searchText}
          setSearchText={setSearchText}
          hideStaleManualIntervention={hideStaleManualIntervention}
          setHideStaleManualIntervention={
            handleHideStaleManualInterventionChange
          }
          staleManualInterventionCount={staleManualInterventionCount}
          isLoading={tableLoading}
          onCopyJobReferences={copyVisibleJobReferences}
          copyJobReferencesDisabled={visibleJobReferences.length < 1}
          copyJobReferencesCount={visibleJobReferences.length}
        />
      </Box>
      {isInitialLayoutReady ? (
        <Box
          ref={customHeaderRef}
          sx={{
            paddingRight: `${scrollbarGutterWidth}px`,
            backgroundColor: headerColour,
          }}
        >
          <table
            style={{
              tableLayout: 'fixed',
              width: '100%',
              borderCollapse: 'collapse',
            }}
          >
            <colgroup>
              {columnWidths.map((width, idx) => (
                <col key={idx} style={{ width }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {COLUMN_HEADERS.map((header, idx) => {
                  const isSorted = sortState.columnId === idx
                  const arrow = isSorted
                    ? sortState.direction === 'asc'
                      ? ' ↑'
                      : ' ↓'
                    : ''
                  return (
                    <th
                      key={idx}
                      onClick={() => handleHeaderSortClick(idx)}
                      style={{
                        backgroundColor: headerColour,
                        color: headerTextColour,
                        fontSize: '12px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        textAlign: 'left',
                        padding: idx === 0 ? '8px 5px 8px 12px' : '8px 5px',
                        cursor: header.sortable ? 'pointer' : 'default',
                        userSelect: 'none',
                      }}
                    >
                      {header.title}
                      {arrow}
                    </th>
                  )
                })}
              </tr>
            </thead>
          </table>
        </Box>
      ) : null}
      {isInitialLayoutReady ? (
        <MaterialTable
          title=""
          tableRef={tableRef}
          data={(query) => fetchTableData(query)}
          isLoading={false}
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
          components={{
            Toolbar: () => <></>,
            OverlayLoading: () => null,
            Pagination: (paginationProps: any) => (
              <TablePagination
                {...paginationProps}
                rowsPerPageOptions={pageSizeOptions.map((option) =>
                  option === allPageSize
                    ? { label: 'All', value: option }
                    : option,
                )}
                SelectProps={{
                  ...paginationProps.SelectProps,
                  renderValue: (value: number | string) => {
                    const numericValue = Number(value)
                    const displayValue =
                      numericValue === allPageSize ? 'All' : numericValue

                    if (paginationProps.SelectProps?.renderValue) {
                      return paginationProps.SelectProps.renderValue(
                        displayValue,
                      )
                    }

                    return displayValue
                  },
                }}
              />
            ),
          }}
          onRowsPerPageChange={(nextPageSize) => {
            const numericPageSize = Number(nextPageSize)
            if (!Number.isFinite(numericPageSize) || numericPageSize < 1) {
              return
            }

            if (numericPageSize === allPageSize) {
              pageSizeModeRef.current = 'all'
            } else {
              pageSizeModeRef.current = 'multiple'
              const nextMultiplier = Math.max(
                1,
                Math.round(numericPageSize / calculatedPageSize),
              )
              pageSizeMultiplierRef.current = nextMultiplier
            }
            pageSizeRef.current = numericPageSize
            setPageSize(numericPageSize)
          }}
          columns={
            [
              {
                title: 'JOB REF',
                field: 'jobReference',
                render: (rowData: any) => <span>{rowData.jobReference}</span>,
              },
              {
                title: 'FLAGS',
                field: 'flags',
                render: (rowData: any) => {
                  const {
                    showMeetAndGreetIcon,
                    showDestinationSupplierIcon,
                    isFlexibleBooking,
                    isImmediateJourney,
                    flightNumber,
                    showFlightNumberIcon,
                    isOnwardJourney,
                    isOfflineBooking,
                  } = getRowFlags(rowData)
                  const iconSx = (theme: any) => ({
                    color: theme.palette.info.dark,
                  })
                  const icons: JSX.Element[] = []
                  if (showMeetAndGreetIcon)
                    icons.push(
                      <Tooltip key="meetAndGreet" title="Meet & Greet">
                        <Assignment fontSize="small" sx={iconSx} />
                      </Tooltip>,
                    )
                  if (showDestinationSupplierIcon)
                    icons.push(
                      <Tooltip
                        key="destinationSupplier"
                        title="Destination Supplier"
                      >
                        <ShareLocationIcon fontSize="small" sx={iconSx} />
                      </Tooltip>,
                    )
                  if (isFlexibleBooking)
                    icons.push(
                      <Tooltip key="flexible" title="Flexible Booking">
                        <AccessTime fontSize="small" sx={iconSx} />
                      </Tooltip>,
                    )
                  if (isImmediateJourney)
                    icons.push(
                      <Tooltip key="immediate" title="Immediate Journey">
                        <EmojiPeopleIcon fontSize="small" sx={iconSx} />
                      </Tooltip>,
                    )
                  if (showFlightNumberIcon)
                    icons.push(
                      <Tooltip key="flight" title={`Flight: ${flightNumber}`}>
                        <FlightOutlined fontSize="small" sx={iconSx} />
                      </Tooltip>,
                    )
                  if (isOnwardJourney)
                    icons.push(
                      <Tooltip key="onward" title="Onward Journey">
                        <DoubleArrow fontSize="small" sx={iconSx} />
                      </Tooltip>,
                    )
                  if (isOfflineBooking)
                    icons.push(
                      <Tooltip
                        key="offline"
                        title={`Offline: ${rowData.supplier.companyName}`}
                      >
                        <WifiOff fontSize="small" sx={iconSx} />
                      </Tooltip>,
                    )

                  const rowStyle = {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '2px',
                  } as const

                  if (icons.length > 3 && icons.length % 2 === 1) {
                    const half = Math.ceil(icons.length / 2)
                    return (
                      <span
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '2px',
                        }}
                      >
                        <span style={rowStyle}>{icons.slice(0, half)}</span>
                        <span style={rowStyle}>{icons.slice(half)}</span>
                      </span>
                    )
                  }

                  return <span style={rowStyle}>{icons}</span>
                },
              },
              {
                title: 'PASSENGER',
                field: 'passengers[0].name',
                render: (rowData: any) => {
                  const leadPassenger =
                    rowData.passengers?.filter(
                      (passenger: any) => passenger.isLeadPassenger === true,
                    ) || ''
                  return leadPassenger[0]?.name
                },
              },
              {
                title: 'CLIENT',
                field: 'client.companyName',
                render: (rowData: any) => {
                  const companyName = rowData.client?.companyName ?? ''
                  const alias = String(rowData.client?.alias ?? '').trim()
                  if (alias) {
                    return (
                      <Tooltip title={companyName}>
                        <span>{alias}</span>
                      </Tooltip>
                    )
                  }
                  return <span>{companyName}</span>
                },
              },
              {
                title: 'Destination Time',
                field: 'when',
                type: 'datetime',
                render: (rowData: any) => {
                  return (
                    <span>
                      {DisplayDate({
                        date: bookedTimeWithEta(
                          rowData.when,
                          rowData.estimatedArrivalTime,
                        ),
                        timeZone: rowData.timeZone,
                        viewType: 'actual',
                      })}
                    </span>
                  )
                },
              },
              {
                title: 'Your TimeZone',
                field: 'when',
                defaultSort: 'asc',
                type: 'datetime',
                render: (rowData: any) => {
                  return (
                    <span>
                      {DisplayDate({
                        date: bookedTimeWithEta(
                          rowData.when,
                          rowData.estimatedArrivalTime,
                        ),
                        timeZone: rowData.timeZone,
                        viewType: 'local',
                      })}
                    </span>
                  )
                },
              },
              {
                title: 'PROGRESS',
                field: 'status.progress',
                render: (rowData: any) => {
                  if (rowData.status?.statusNote) {
                    return (
                      <Tooltip title={rowData.status.statusNote}>
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            textWrap: 'nowrap',
                            flexWrap: 'nowrap',
                          }}
                        >
                          {Object.entries(BookingSupplierStatus).find(
                            (status) => status[0] === rowData.status?.progress,
                          )?.[1] ?? (rowData.status?.progress as string)}
                          <Info
                            fontSize="medium"
                            sx={(theme) => ({
                              color: theme.palette.info.dark,
                              paddingLeft: '5px',
                              verticalAlign: 'bottom',
                            })}
                          />
                        </span>
                      </Tooltip>
                    )
                  } else {
                    return (
                      <span style={{ display: 'flex', flexWrap: 'nowrap' }}>
                        {Object.entries(BookingSupplierStatus).find(
                          (status) => status[0] === rowData.status?.progress,
                        )?.[1] ?? (rowData.status?.progress as string)}
                      </span>
                    )
                  }
                },
              },
              {
                title: 'EXPECTED',
                field: 'status.estimatedArrivalTime',
                sorting: false,
                render: (rowData: any) => {
                  const destinationTimeExpected = DateTime.fromISO(
                    String(rowData.when ?? ''),
                  )
                    .setZone(rowData.timeZone || 'Europe/London')
                    .plus({
                      minutes: rowData.estimatedArrivalTime
                        ? rowData.estimatedArrivalTime
                        : 0,
                    })
                    .toJSDate()
                  return (
                    <span>
                      {expectedArrivalStatusDisplay(
                        rowData.status?.progress,
                        destinationTimeExpected,
                        rowData.status?.estimatedArrivalTime
                          ? rowData.status.estimatedArrivalTime
                          : 0,
                        rowData.timeZone || 'Europe/London',
                      )}
                    </span>
                  )
                },
              },
              {
                title: 'STATUS',
                field: 'status.status',
                render: (rowData: any) => {
                  return (
                    <StatusChip
                      status={rowData.status?.status}
                      statusType={'VehicleJobStatus'}
                    />
                  )
                },
              },
            ].map((col, idx) => ({
              ...col,
              cellStyle: {
                ...((col as any).cellStyle ?? {}),
                width: columnWidths[idx],
              },
              sorting: false,
            })) as any
          }
          options={{
            tableLayout: 'fixed',
            headerStyle: {
              backgroundColor: headerColour,
              color: headerTextColour,
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'uppercase',
            },
            rowStyle: (rowData) => ({
              padding: '0 !important',
              margin: '0 !important',
              fontSize: '12px',
              backgroundColor:
                selectedJob?.id != null &&
                String(selectedJob.id) === String(rowData.id)
                  ? '#FFFFFF'
                  : '#F8F8F8',
            }),
            paging: true,
            pageSizeOptions,
            pageSize,
            minBodyHeight: tableBodyHeight,
            maxBodyHeight: tableBodyHeight,
            overflowY: isStableScrollbarGutterSupported() ? 'auto' : 'scroll',
            search: false,
            maxColumnSort: 1,
            thirdSortClick: false,
            draggable: false,
          }}
          onRowClick={(_event: any, rowData: any) => {
            if (
              selectedJob?.id != null &&
              String(selectedJob.id) === String(rowData.id)
            ) {
              clearSelection()
              return
            }

            setSelectedJob(rowData)
          }}
        />
      ) : null}
    </Box>
  )
}

export default Table
